/**
 * @license
 * © CROUIN 2021. All rights reserved.
 * Powered by MaxDesign.
 * ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
 * ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 */

class Faq extends HTMLElement {
  connectedCallback() {
    this.settings = this.json(this);
    this.items = this.querySelectorAll('.faqmd__item');
    this.items.forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
    this.settings.search.enable && this.search();
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Tab Interactions */
  onItemClick(e) {
    const item = e.currentTarget;
    const prevItem = this.querySelector('.faqmd__item[data-open]');
    const prevItemRect = prevItem?.getBoundingClientRect();
    // toggle tabs
    this.items.forEach(el => el.toggleAttribute('data-open', el == item && !el.hasAttribute('data-open')));
    // transition item
    const { content, contentHeight } = this.contentHeight(item);
    if (prevItemRect?.top < 0 && prevItemRect?.bottom > 0) {
      item.dataset.transitionDisabled = prevItem.dataset.transitionDisabled = '';
      content.style.height = 'auto';
      content.style.height = contentHeight + 'px';
      this.scrollBehavior = 'auto';
      scrollTo({ top: pageYOffset + item.getBoundingClientRect().top - 100 });
      this.scrollBehavior = '';
    } else {
      item.removeAttribute('data-transition-disabled');
      setTimeout(() => (content.style.height = contentHeight + 'px'));
    }
    // default to auto if an image is not loaded
    const img = content.querySelector('img');
    if (img && !img.complete) content.style.height = 'auto';
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Fuse Search */

  /**
   * We load fuse asynchronously for performance purposes
   */
  search() {
    const { search } = this.settings;
    let fuse;
    const minChars = 4;
    const searchInput = this.querySelector('[data-search-input]');
    const searchResults = this.querySelector('[data-search-result]');
    const searchClose = () => (searchInput.removeAttribute('data-active'), (searchInput.value = ''));

    /* on search blur */
    searchInput.addEventListener('blur', e => {
      const relatedTarget = e.relatedTarget || document.activeElement;
      if (!searchResults.contains(relatedTarget)) searchClose();
    });

    /* on result blur */
    searchResults.addEventListener('focusout', e => {
      const relatedTarget = e.relatedTarget || document.activeElement;
      if (!searchInput.contains(relatedTarget) && !searchResults.contains(relatedTarget)) searchClose();
    });

    /* on result click */
    searchResults.addEventListener('click', e => {
      const listIndex = e.target?.dataset?.index;
      if (listIndex) {
        const tab = this.items[listIndex];
        this.scrollBehavior = 'auto';
        scrollBy({ top: tab.getBoundingClientRect().top - 100 });
        this.scrollBehavior = '';
        tab.click();
        searchClose();
      }
    });

    /* on search input */
    searchInput.addEventListener('input', async () => {
      const inputValue = searchInput.value.trim();
      if (!fuse) {
        await new Promise(resolve =>
          this.appendChild(Object.assign(document.createElement('script'), { src: search.asset, onload: resolve }))
        );
        fuse = new Fuse(search.data, {
          keys: [{ name: 'question', weight: 1.5 }, 'answer'],
          threshold: 0.4,
          minMatchCharLength: minChars,
          ignoreLocation: true,
        });
      }
      // generate results
      const results = fuse.search(inputValue);
      searchResults.innerHTML = '';
      if (results.length) {
        results.slice(0, 5).forEach(result => {
          searchResults.innerHTML += `<li tabindex="0" data-index="${result.refIndex}">${result.item.question}</li>`;
        });
        searchInput.setAttribute('data-active', '');
      } else if (searchInput.value.length >= minChars && search.failOutput) {
        searchResults.innerHTML = `<li>${search.failOutput}</li>`;
        searchInput.setAttribute('data-active', '');
      } else searchInput.removeAttribute('data-active');
    });
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */

  /**
   * @returns {Object} custom element settings
   */
  get json() {
    return scope => JSON.parse(scope.querySelector(':scope > [type="application/json"]')?.innerText || '{}');
  }

  /**
   * get tab content height for height animation
   * @param {Object} tab - clicked tab
   * @returns {Object} - tab content Node & content height integer
   */
  get contentHeight() {
    return item => {
      const content = item.lastElementChild;
      content.style.height = 'auto';
      const contentHeight = content.offsetHeight;
      content.style.height = '0px';
      return { content, contentHeight };
    };
  }

  set scrollBehavior(behavior) {
    if (behavior) document.documentElement.style.scrollBehavior = behavior;
    else document.documentElement.style.removeProperty('scrollBehavior');
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */
}
customElements.define('faq-maxdesign', Faq);
